import React, { useEffect, useMemo, useRef, useState } from 'react';
import { siteEditor, files as filesApi } from '../services/api';
import { toast } from '../hooks/use-toast';
import { INTERNAL_ROUTES, INTERNAL_ROUTE_GROUPS, SITE_PAGE_LABELS, SECTION_TYPES } from '../constants/siteEditor';
import {
  Loader2, Plus, Trash2, Edit3, Eye, EyeOff, Upload, ArrowUp, ArrowDown,
  Save, X, Link as LinkIcon, ExternalLink, FileText, Image as ImageIcon,
  Layout, Globe, MousePointer2,
} from 'lucide-react';

// Blank content templates keyed by section type. Used by "Add Section".
const BLANK_CONTENT = {
  heading: { text: 'New heading', level: 'h2', align: 'left', vPos: 'middle' },
  richtext: { text: '', align: 'left', vPos: 'middle' },
  image: { url: '', alt: '', caption: '', align: 'center', vPos: 'middle' },
  gallery: { images: [], align: 'center', vPos: 'middle' },
  banner: { image: '', headline: '', subtext: '', buttonLabel: '', buttonHref: '', buttonTarget: '_self', align: 'center', vPos: 'middle' },
  buttons: { align: 'center', buttons: [], vPos: 'middle' },
  video: { url: '', align: 'center', vPos: 'middle' },
  divider: { vPos: 'middle' },
};

// Which types honor horizontal alignment in the placement panel.
const H_ALIGN_TYPES = new Set(['heading', 'richtext', 'image', 'gallery', 'banner', 'buttons', 'video']);

// ---------- Upload helper ----------
async function uploadFile(file, kind = 'business') {
  const r = await filesApi.upload(file, kind);
  return r.url; // path like /api/files/{APP_NAME}/uploads/{uid}/{uuid}.{ext}
}

// ---------- Small file/image upload button ----------
function UploadButton({ onUploaded, label = 'Upload', kind = 'business', testId, accept = 'image/*' }) {
  const inp = useRef(null);
  const [busy, setBusy] = useState(false);
  return (
    <>
      <input ref={inp} type="file" accept={accept} className="hidden"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          e.target.value = '';
          if (!f) return;
          setBusy(true);
          try {
            const url = await uploadFile(f, kind);
            onUploaded(url, f);
          } catch (err) {
            toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try again' });
          } finally { setBusy(false); }
        }} />
      <button type="button" data-testid={testId} onClick={() => inp.current?.click()} disabled={busy}
        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-200 hover:border-green-500 text-xs disabled:opacity-60">
        {busy ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {label}
      </button>
    </>
  );
}

// ---------- Button link editor (used in Buttons + Banner) ----------
function ButtonLinkEditor({ value, onChange, testPrefix }) {
  const v = value || {};
  const [mode, setMode] = useState(() => {
    const h = (v.href || '').trim();
    if (!h) return 'internal';
    if (h.startsWith('mailto:')) return 'mailto';
    if (h.startsWith('tel:')) return 'tel';
    if (h.startsWith('/api/')) return 'file';
    if (/^https?:\/\//i.test(h)) return 'external';
    return 'internal';
  });
  const update = (patch) => onChange({ ...v, ...patch });
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1">
        {[
          ['internal', 'Internal page'],
          ['external', 'External URL'],
          ['file', 'Uploaded file'],
          ['mailto', 'Email'],
          ['tel', 'Phone'],
        ].map(([k, lbl]) => (
          <button key={k} type="button" data-testid={`${testPrefix}-mode-${k}`}
            onClick={() => setMode(k)}
            className={`px-2 py-1 rounded text-xs border ${mode === k ? 'border-green-500 bg-green-500/10 text-green-200' : 'border-[#2a2a2a] text-gray-400'}`}>
            {lbl}
          </button>
        ))}
      </div>
      {mode === 'internal' && (
        <select data-testid={`${testPrefix}-internal`} value={v.href || ''} onChange={(e) => update({ href: e.target.value })}
          className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm">
          <option value="">Pick a page…</option>
          {INTERNAL_ROUTE_GROUPS.map(g => (
            <optgroup key={g.label} label={g.label}>
              {g.routes.map(r => {
                const label = `${r.label} (${r.value})`;
                return <option key={r.value} value={r.value}>{label}</option>;
              })}
            </optgroup>
          ))}
        </select>
      )}
      {mode === 'external' && (
        <input data-testid={`${testPrefix}-external`} type="url" placeholder="https://example.com"
          value={v.href || ''} onChange={(e) => update({ href: e.target.value })}
          className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
      )}
      {mode === 'file' && (
        <div className="flex items-center gap-2">
          <input readOnly value={v.href || ''} placeholder="Upload a file →"
            className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm text-gray-300" />
          <UploadButton testId={`${testPrefix}-file-upload`} kind="business"
            accept="image/*,application/pdf,.pdf,.doc,.docx"
            label="Upload file"
            onUploaded={(url) => update({ href: url })} />
        </div>
      )}
      {mode === 'mailto' && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">mailto:</span>
          <input data-testid={`${testPrefix}-mailto`} type="email" placeholder="hello@pbb.app"
            value={(v.href || '').replace(/^mailto:/, '')}
            onChange={(e) => update({ href: e.target.value ? `mailto:${e.target.value}` : '' })}
            className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        </div>
      )}
      {mode === 'tel' && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-400">tel:</span>
          <input data-testid={`${testPrefix}-tel`} type="tel" placeholder="+1 555 555 5555"
            value={(v.href || '').replace(/^tel:/, '')}
            onChange={(e) => update({ href: e.target.value ? `tel:${e.target.value.replace(/\s+/g, '')}` : '' })}
            className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        </div>
      )}
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <label className="inline-flex items-center gap-1">
          <input type="checkbox" checked={v.target === '_blank'}
            onChange={(e) => update({ target: e.target.checked ? '_blank' : '_self' })} />
          Open in new tab
        </label>
      </div>
    </div>
  );
}

// ---------- Shared placement panel (horizontal align + vertical position) ----------
function PositionPanel({ type, content, setContent }) {
  const set = (patch) => setContent({ ...content, ...patch });
  const showHAlign = H_ALIGN_TYPES.has(type);
  return (
    <div className="mt-2 pt-3 border-t border-[#1f1f1f] space-y-2" data-testid="se-position-panel">
      <div className="text-[10px] uppercase tracking-wider text-gray-400 font-semibold">Placement</div>
      <div className={`grid gap-2 ${showHAlign ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {showHAlign && (
          <div>
            <div className="text-[11px] text-gray-500 mb-1">Horizontal alignment</div>
            <div className="flex gap-1" data-testid="se-halign-group">
              {['left', 'center', 'right'].map(a => (
                <button key={a} type="button" data-testid={`se-halign-${a}`}
                  onClick={() => set({ align: a })}
                  className={`flex-1 px-2 py-1.5 rounded text-xs border ${
                    (content.align || 'center') === a
                      ? 'border-green-500 bg-green-500/10 text-green-200'
                      : 'border-[#2a2a2a] text-gray-400'}`}>
                  {a[0].toUpperCase() + a.slice(1)}
                </button>
              ))}
            </div>
          </div>
        )}
        <div>
          <div className="text-[11px] text-gray-500 mb-1">Vertical position on page</div>
          <div className="flex gap-1" data-testid="se-vpos-group">
            {['top', 'middle', 'bottom'].map(v => (
              <button key={v} type="button" data-testid={`se-vpos-${v}`}
                onClick={() => set({ vPos: v })}
                className={`flex-1 px-2 py-1.5 rounded text-xs border ${
                  (content.vPos || 'middle') === v
                    ? 'border-green-500 bg-green-500/10 text-green-200'
                    : 'border-[#2a2a2a] text-gray-400'}`}>
                {v[0].toUpperCase() + v.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Content forms per section type ----------
function ContentForm({ type, content, setContent }) {
  const set = (patch) => setContent({ ...content, ...patch });
  if (type === 'heading') {
    return (
      <div className="space-y-2">
        <input data-testid="se-heading-text" value={content.text || ''} onChange={(e) => set({ text: e.target.value })}
          placeholder="Heading text" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
        <select data-testid="se-heading-level" value={content.level || 'h2'} onChange={(e) => set({ level: e.target.value })}
          className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm">
          <option value="h1">H1 (Big)</option>
          <option value="h2">H2 (Medium)</option>
          <option value="h3">H3 (Small)</option>
        </select>
      </div>
    );
  }
  if (type === 'richtext') {
    return (
      <div className="space-y-2">
        <textarea data-testid="se-text-content" rows={6} value={content.text || ''} onChange={(e) => set({ text: e.target.value })}
          placeholder="Write your paragraph… line breaks are preserved."
          className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
      </div>
    );
  }
  if (type === 'image') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input readOnly value={content.url || ''} placeholder="Upload an image →"
            className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm text-gray-300" />
          <UploadButton testId="se-image-upload" onUploaded={(url) => set({ url })} label="Upload" />
        </div>
        {content.url && <img src={filesApi.urlFor(content.url)} alt="preview" className="rounded-lg max-h-40 border border-[#1f1f1f]" />}
        <input data-testid="se-image-alt" value={content.alt || ''} onChange={(e) => set({ alt: e.target.value })}
          placeholder="Alt text (for accessibility)" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        <input data-testid="se-image-caption" value={content.caption || ''} onChange={(e) => set({ caption: e.target.value })}
          placeholder="Caption (optional)" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
      </div>
    );
  }
  if (type === 'gallery') {
    const imgs = Array.isArray(content.images) ? content.images : [];
    const updateImg = (i, patch) => {
      const next = [...imgs]; next[i] = { ...next[i], ...patch }; set({ images: next });
    };
    const removeImg = (i) => set({ images: imgs.filter((_, idx) => idx !== i) });
    return (
      <div className="space-y-2">
        <UploadButton testId="se-gallery-add" label="Add image"
          onUploaded={(url) => set({ images: [...imgs, { url, alt: '', caption: '' }] })} />
        {imgs.length === 0 && <div className="text-xs text-gray-500">No images yet — upload one above.</div>}
        <div className="space-y-2">
          {imgs.map((img, i) => (
            <div key={i} className="flex items-start gap-2 border border-[#1f1f1f] rounded-lg p-2">
              <img src={filesApi.urlFor(img.url)} alt="" className="w-16 h-16 object-cover rounded" />
              <div className="flex-1 space-y-1">
                <input value={img.alt || ''} onChange={(e) => updateImg(i, { alt: e.target.value })}
                  placeholder="Alt text" className="w-full bg-black border border-[#1f1f1f] rounded px-2 py-1 text-xs" />
                <input value={img.caption || ''} onChange={(e) => updateImg(i, { caption: e.target.value })}
                  placeholder="Caption" className="w-full bg-black border border-[#1f1f1f] rounded px-2 py-1 text-xs" />
              </div>
              <button type="button" onClick={() => removeImg(i)}
                data-testid={`se-gallery-remove-${i}`}
                className="p-1 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 size={12} /></button>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (type === 'banner') {
    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <input readOnly value={content.image || ''} placeholder="Upload a background image →"
            className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm text-gray-300" />
          <UploadButton testId="se-banner-upload" onUploaded={(url) => set({ image: url })} label="Upload" />
        </div>
        {content.image && <img src={filesApi.urlFor(content.image)} alt="preview" className="rounded-lg max-h-40 border border-[#1f1f1f]" />}
        <input data-testid="se-banner-headline" value={content.headline || ''} onChange={(e) => set({ headline: e.target.value })}
          placeholder="Headline" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        <textarea data-testid="se-banner-subtext" rows={2} value={content.subtext || ''} onChange={(e) => set({ subtext: e.target.value })}
          placeholder="Subtext" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        <input data-testid="se-banner-btn-label" value={content.buttonLabel || ''} onChange={(e) => set({ buttonLabel: e.target.value })}
          placeholder="Button label (optional)" className="w-full bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
        {content.buttonLabel && (
          <ButtonLinkEditor
            value={{ href: content.buttonHref, target: content.buttonTarget }}
            onChange={(v) => set({ buttonHref: v.href || '', buttonTarget: v.target || '_self' })}
            testPrefix="se-banner-btn" />
        )}
      </div>
    );
  }
  if (type === 'buttons') {
    const btns = Array.isArray(content.buttons) ? content.buttons : [];
    const updateBtn = (i, patch) => {
      const next = [...btns]; next[i] = { ...next[i], ...patch }; set({ buttons: next });
    };
    const removeBtn = (i) => set({ buttons: btns.filter((_, idx) => idx !== i) });
    return (
      <div className="space-y-2">
        <button type="button" data-testid="se-buttons-add"
          onClick={() => set({ buttons: [...btns, { label: 'New button', href: '', style: 'primary', target: '_self' }] })}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-xs font-semibold">
          <Plus size={12} /> Add button
        </button>
        {btns.length === 0 && <div className="text-xs text-gray-500">No buttons yet — click "Add button" above.</div>}
        {btns.map((b, i) => (
          <div key={i} className="border border-[#1f1f1f] rounded-lg p-3 space-y-2 bg-[#0d1f14]/50">
            <div className="flex items-center gap-2">
              <input data-testid={`se-btn-label-${i}`} value={b.label || ''} onChange={(e) => updateBtn(i, { label: e.target.value })}
                placeholder="Button label" className="flex-1 bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm" />
              <select value={b.style || 'primary'} onChange={(e) => updateBtn(i, { style: e.target.value })}
                className="bg-black border border-[#1f1f1f] rounded-lg px-2 py-1.5 text-sm">
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="ghost">Ghost</option>
              </select>
              <button type="button" onClick={() => removeBtn(i)}
                data-testid={`se-btn-remove-${i}`}
                className="p-2 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 size={12} /></button>
            </div>
            <ButtonLinkEditor value={b} onChange={(v) => updateBtn(i, v)} testPrefix={`se-btn-${i}`} />
          </div>
        ))}
      </div>
    );
  }
  if (type === 'video') {
    return (
      <div className="space-y-2">
        <input data-testid="se-video-url" value={content.url || ''} onChange={(e) => set({ url: e.target.value })}
          placeholder="YouTube URL (https://youtube.com/watch?v=...)"
          className="w-full bg-black border border-[#1f1f1f] rounded-lg px-3 py-2 text-sm" />
      </div>
    );
  }
  if (type === 'divider') {
    return <div className="text-xs text-gray-500">A thin horizontal line will render on the public page.</div>;
  }
  return null;
}

// ---------- Section list row ----------
function SectionRow({ section, onEdit, onDelete, onPublish, onUnpublish, onMoveUp, onMoveDown, canUp, canDown, busyId }) {
  const s = section;
  const preview = useMemo(() => {
    const c = s.draft || {};
    if (s.type === 'heading') return c.text || '(empty heading)';
    if (s.type === 'richtext') return (c.text || '').slice(0, 120) || '(empty text)';
    if (s.type === 'image') return c.alt || c.caption || (c.url ? 'Image' : '(no image)');
    if (s.type === 'gallery') return `${(c.images || []).length} image(s)`;
    if (s.type === 'banner') return c.headline || '(no headline)';
    if (s.type === 'buttons') return `${(c.buttons || []).length} button(s)`;
    if (s.type === 'video') return c.url || '(no URL)';
    if (s.type === 'divider') return '— Divider —';
    return s.type;
  }, [s]);
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-[#1f1f1f] bg-[#0a140d]"
         data-testid={`site-section-row-${s.id}`}>
      <div className="flex flex-col gap-1">
        <button type="button" disabled={!canUp || busyId === s.id} onClick={() => onMoveUp(s.id)}
          data-testid={`site-section-up-${s.id}`}
          className="p-1 rounded border border-[#2a2a2a] text-gray-300 disabled:opacity-30 hover:border-green-500">
          <ArrowUp size={12} />
        </button>
        <button type="button" disabled={!canDown || busyId === s.id} onClick={() => onMoveDown(s.id)}
          data-testid={`site-section-down-${s.id}`}
          className="p-1 rounded border border-[#2a2a2a] text-gray-300 disabled:opacity-30 hover:border-green-500">
          <ArrowDown size={12} />
        </button>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-green-500/40 text-green-300 bg-green-500/10">
            {s.type}
          </span>
          {(() => {
            const vp = (s.draft && s.draft.vPos) || 'middle';
            return (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-sky-500/40 text-sky-300 bg-sky-500/10"
                data-testid={`site-section-vpos-${s.id}`}>
                {vp}
              </span>
            );
          })()}
          {H_ALIGN_TYPES.has(s.type) && (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-fuchsia-500/40 text-fuchsia-300 bg-fuchsia-500/10"
              data-testid={`site-section-halign-${s.id}`}>
              {(s.draft && s.draft.align) || 'center'}
            </span>
          )}
          {s.status === 'published' ? (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-emerald-400/40 text-emerald-300 bg-emerald-500/10">
              Live
            </span>
          ) : s.status === 'unpublished' ? (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-yellow-500/40 text-yellow-300 bg-yellow-500/10">
              Hidden
            </span>
          ) : (
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-neutral-500/40 text-neutral-300 bg-neutral-500/10">
              Draft
            </span>
          )}
        </div>
        <div className="mt-1 text-sm text-gray-200 truncate">{preview}</div>
      </div>
      <div className="flex items-center gap-1">
        <button type="button" onClick={() => onEdit(s)} data-testid={`site-section-edit-${s.id}`}
          className="p-2 rounded border border-[#2a2a2a] text-gray-200 hover:border-green-500"><Edit3 size={12} /></button>
        {s.status === 'published' ? (
          <button type="button" onClick={() => onUnpublish(s.id)} disabled={busyId === s.id}
            data-testid={`site-section-unpublish-${s.id}`}
            className="p-2 rounded border border-[#2a2a2a] text-yellow-300 hover:border-yellow-400"><EyeOff size={12} /></button>
        ) : (
          <button type="button" onClick={() => onPublish(s.id)} disabled={busyId === s.id}
            data-testid={`site-section-publish-${s.id}`}
            className="p-2 rounded border border-[#2a2a2a] text-emerald-300 hover:border-emerald-400"><Eye size={12} /></button>
        )}
        <button type="button" onClick={() => onDelete(s)} disabled={busyId === s.id}
          data-testid={`site-section-delete-${s.id}`}
          className="p-2 rounded border border-red-500/40 text-red-300 hover:bg-red-500/10"><Trash2 size={12} /></button>
      </div>
    </div>
  );
}

// ---------- Add-Section picker ----------
function AddSectionMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const iconFor = (t) => {
    if (t === 'heading') return <Layout size={14} />;
    if (t === 'richtext') return <FileText size={14} />;
    if (t === 'image') return <ImageIcon size={14} />;
    if (t === 'gallery') return <ImageIcon size={14} />;
    if (t === 'banner') return <Globe size={14} />;
    if (t === 'buttons') return <MousePointer2 size={14} />;
    if (t === 'video') return <LinkIcon size={14} />;
    return <FileText size={14} />;
  };
  return (
    <div className="relative">
      <button type="button" data-testid="site-add-section-open" onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-semibold">
        <Plus size={14} /> Add Section
      </button>
      {open && (
        <>
          <div className="fixed inset-0" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-20 bg-[#0a140d] border border-[#1f1f1f] rounded-lg shadow-xl min-w-[220px] py-1"
               data-testid="site-add-section-menu">
            {SECTION_TYPES.map(t => (
              <button key={t.value} type="button" data-testid={`site-add-section-${t.value}`}
                onClick={() => { onPick(t.value); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm text-gray-200 hover:bg-[#12271b] inline-flex items-center gap-2">
                {iconFor(t.value)} {t.label}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// ---------- Edit modal ----------
function EditModal({ section, onSave, onCancel, saving }) {
  const [content, setContent] = useState(section.draft || {});
  useEffect(() => { setContent(section.draft || {}); }, [section.id]); // eslint-disable-line
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start md:items-center justify-center p-4 overflow-auto"
         data-testid="site-edit-modal">
      <div className="bg-[#0a140d] border border-[#1f1f1f] rounded-2xl w-full max-w-2xl shadow-2xl">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#1f1f1f]">
          <div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">{section.type}</div>
            <div className="text-lg font-semibold text-white">Edit section</div>
          </div>
          <button type="button" onClick={onCancel} data-testid="site-edit-close"
            className="p-2 rounded border border-[#2a2a2a] text-gray-300 hover:text-white"><X size={14} /></button>
        </div>
        <div className="p-4 space-y-3">
          <ContentForm type={section.type} content={content} setContent={setContent} />
          <PositionPanel type={section.type} content={content} setContent={setContent} />
        </div>
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#1f1f1f]">
          <button type="button" onClick={onCancel} data-testid="site-edit-cancel"
            className="px-3 py-1.5 rounded-lg border border-[#2a2a2a] text-gray-300 text-sm">Cancel</button>
          <button type="button" onClick={() => onSave(content)} disabled={saving} data-testid="site-edit-save"
            className="inline-flex items-center gap-1 px-4 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-black text-sm font-semibold disabled:opacity-60">
            {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save draft
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------- Main panel ----------
export default function AdminSiteEditorPanel() {
  const [pages, setPages] = useState([]);
  const [pageKey, setPageKey] = useState('home');
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadPages = async () => {
    try { setPages(await siteEditor.pages()); }
    catch (err) { toast({ title: 'Failed to load pages', description: err?.response?.data?.detail || '' }); }
  };
  const loadSections = async (k) => {
    setLoading(true);
    try { setSections(await siteEditor.list(k)); }
    catch (err) { toast({ title: 'Failed to load sections', description: err?.response?.data?.detail || '' }); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadPages(); }, []);
  useEffect(() => { loadSections(pageKey); }, [pageKey]);

  const addSection = async (type) => {
    try {
      await siteEditor.create(pageKey, { type, content: BLANK_CONTENT[type] || {} });
      toast({ title: 'Section added', description: 'Edit → Publish to make it live.' });
      await Promise.all([loadSections(pageKey), loadPages()]);
    } catch (err) { toast({ title: 'Add failed', description: err?.response?.data?.detail || 'Try again' }); }
  };

  const saveEdit = async (content) => {
    if (!editing) return;
    setSaving(true);
    try {
      await siteEditor.update(editing.id, { content });
      toast({ title: 'Draft saved' });
      setEditing(null);
      await loadSections(pageKey);
    } catch (err) { toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setSaving(false); }
  };

  const doPublish = async (sid) => {
    setBusyId(sid);
    try { await siteEditor.publish(sid); toast({ title: 'Published' }); await loadSections(pageKey); }
    catch (err) { toast({ title: 'Publish failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusyId(null); }
  };
  const doUnpublish = async (sid) => {
    setBusyId(sid);
    try { await siteEditor.unpublish(sid); toast({ title: 'Hidden from public page' }); await loadSections(pageKey); }
    catch (err) { toast({ title: 'Failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusyId(null); }
  };
  const doDelete = async (s) => {
    const label = s.type;
    if (!window.confirm(`Delete this ${label} section? This cannot be undone.`)) return;
    setBusyId(s.id);
    try { await siteEditor.remove(s.id); toast({ title: 'Section deleted' }); await Promise.all([loadSections(pageKey), loadPages()]); }
    catch (err) { toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again' }); }
    finally { setBusyId(null); }
  };

  const move = async (id, dir) => {
    const idx = sections.findIndex(x => x.id === id);
    if (idx < 0) return;
    const j = dir === 'up' ? idx - 1 : idx + 1;
    if (j < 0 || j >= sections.length) return;
    const reordered = [...sections];
    [reordered[idx], reordered[j]] = [reordered[j], reordered[idx]];
    setSections(reordered);
    setBusyId(id);
    try { await siteEditor.reorder(pageKey, reordered.map(x => x.id)); }
    catch (err) { toast({ title: 'Reorder failed', description: err?.response?.data?.detail || 'Try again' }); await loadSections(pageKey); }
    finally { setBusyId(null); }
  };

  const previewUrlByKey = {
    home: '/marketing',
    upgrade: '/business/upgrade',
  };
  const previewUrl = previewUrlByKey[pageKey] || `/${pageKey}`;

  return (
    <div className="space-y-4" data-testid="admin-site-editor">
      {/* Header */}
      <div className="pbb-card p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-wider text-green-300 font-semibold">Public Site Editor</div>
            <h2 className="text-xl font-black text-white mt-1">Edit any public page, without touching code</h2>
            <p className="text-sm text-gray-400 mt-1">Add sections, upload images/files, create buttons that link to any page or URL. Nothing shows on the public site until you publish it.</p>
          </div>
          <a href={previewUrl} target="_blank" rel="noopener noreferrer"
             data-testid="site-preview-link"
             className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-[#2a2a2a] text-gray-200 hover:border-green-500 text-sm">
            <ExternalLink size={14} /> View public page
          </a>
        </div>
      </div>

      {/* Page picker */}
      <div className="pbb-card p-3">
        <div className="text-xs text-gray-400 mb-2">Choose a page to edit</div>
        <div className="flex flex-wrap gap-2" data-testid="site-page-picker">
          {pages.map(p => (
            <button key={p.key} type="button" onClick={() => setPageKey(p.key)}
              data-testid={`site-page-${p.key}`}
              className={`px-3 py-1.5 rounded-full text-xs border ${
                pageKey === p.key
                  ? 'border-green-500 bg-green-500/15 text-green-200'
                  : 'border-[#1f1f1f] text-gray-300 hover:border-green-500/60'
              }`}>
              {SITE_PAGE_LABELS[p.key] || p.key}
              <span className="ml-1 text-[10px] text-gray-500">({p.count})</span>
            </button>
          ))}
        </div>
      </div>

      {/* Toolbar */}
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm text-gray-300">
          <span className="font-semibold">{SITE_PAGE_LABELS[pageKey] || pageKey}</span>
          <span className="text-gray-500"> · {sections.length} section{sections.length === 1 ? '' : 's'}</span>
        </div>
        <AddSectionMenu onPick={addSection} />
      </div>

      {/* Sections list */}
      {loading ? (
        <div className="pbb-card p-10 text-center">
          <Loader2 className="mx-auto animate-spin text-green-400" size={22} />
        </div>
      ) : sections.length === 0 ? (
        <div className="pbb-card p-10 text-center" data-testid="site-empty-state">
          <Layout className="mx-auto text-green-400" size={26} />
          <div className="mt-3 font-semibold">No sections yet on this page</div>
          <p className="text-sm text-gray-400 mt-1 max-w-md mx-auto">
            Click <span className="text-green-300 font-semibold">Add Section</span> above to insert a heading, banner,
            image, button row, video or divider. Nothing shows on the public page until you publish.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {sections.map((s, i) => (
            <SectionRow key={s.id} section={s} busyId={busyId}
              canUp={i > 0} canDown={i < sections.length - 1}
              onMoveUp={() => move(s.id, 'up')} onMoveDown={() => move(s.id, 'down')}
              onEdit={setEditing}
              onDelete={doDelete}
              onPublish={doPublish}
              onUnpublish={doUnpublish} />
          ))}
        </div>
      )}

      {editing && (
        <EditModal section={editing} onCancel={() => setEditing(null)} onSave={saveEdit} saving={saving} />
      )}
    </div>
  );
}
